import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  BarChart3, BookOpen, ClipboardCheck, CloudOff, History, LayoutDashboard, ListTodo, LogOut, Menu, RefreshCw, Store, UserCog, Users, X,
} from 'lucide-react';
import { ROLE_LABELS, type Permission } from '@bp/shared';
import { useAuth } from '@/lib/auth';
import { useOutbox } from '@/lib/outbox';
import { cn } from '@/lib/utils';

type NavItem = { to: string; label: string; icon: typeof Store; perm?: Permission; end?: boolean; hideForStore?: boolean };

const MAIN: NavItem[] = [
  { to: '/', label: 'Overview', icon: LayoutDashboard, perm: 'view_dashboards', end: true },
  { to: '/stores', label: 'Stores', icon: Store, perm: 'view_stores' },
  { to: '/audits', label: 'Audits', icon: ClipboardCheck, perm: 'view_audits' },
  { to: '/tasks', label: 'Action items', icon: ListTodo, perm: 'view_action_items' },
  { to: '/compare', label: 'Practice vs official', icon: BarChart3, perm: 'view_audits' },
  { to: '/templates', label: 'Templates', icon: BookOpen, perm: 'view_templates' },
];
const ADMIN: NavItem[] = [
  { to: '/admin/users', label: 'Users', icon: Users, perm: 'manage_users' },
  { to: '/admin/stores', label: 'Store setup', icon: UserCog, perm: 'edit_stores' },
  { to: '/admin/activity', label: 'Activity log', icon: History, perm: 'view_activity_log' },
];

function NavList({ items, onNavigate }: { items: NavItem[]; onNavigate?: () => void }) {
  return (
    <ul className="space-y-0.5">
      {items.map((i) => (
        <li key={i.to}>
          <NavLink
            to={i.to}
            end={i.end}
            onClick={onNavigate}
            className={({ isActive }) => cn(
              'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
              isActive ? 'bg-primary text-primary-fg' : 'text-fg/80 hover:bg-muted',
            )}
          >
            <i.icon className="size-4.5 flex-none" />
            {i.label}
          </NavLink>
        </li>
      ))}
    </ul>
  );
}

function SyncIndicator() {
  const { count, error, online } = useOutbox();
  if (online && count === 0 && !error) return null;
  return (
    <div className={cn('mx-3 mb-3 rounded-lg px-3 py-2 text-xs', error ? 'bg-fail-bg text-fail' : 'bg-warn-bg text-warn')}>
      <div className="flex items-center gap-2 font-medium">
        {online ? <RefreshCw className="size-3.5 animate-spin" /> : <CloudOff className="size-3.5" />}
        {online ? `Syncing ${count} change${count === 1 ? '' : 's'}…` : count ? `Offline — ${count} change${count === 1 ? '' : 's'} saved on this device` : 'Offline'}
      </div>
      {error && <p className="mt-1">{error}</p>}
    </div>
  );
}

function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { me, can, logout } = useAuth();
  const main = MAIN.filter((i) => !i.perm || can(i.perm));
  const admin = ADMIN.filter((i) => !i.perm || can(i.perm));
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2.5 px-5 py-5">
        <img src="/icon.svg" alt="" className="size-8" />
        <div>
          <p className="leading-tight font-semibold">BluePrint</p>
          <p className="text-xs leading-tight text-muted-fg">Operations audits</p>
        </div>
      </div>
      <nav className="flex-1 space-y-5 overflow-y-auto px-3">
        <NavList items={main} onNavigate={onNavigate} />
        {admin.length > 0 && (
          <div>
            <p className="mb-1 px-3 text-xs font-semibold tracking-wide text-muted-fg uppercase">Administration</p>
            <NavList items={admin} onNavigate={onNavigate} />
          </div>
        )}
      </nav>
      <SyncIndicator />
      <div className="border-t px-3 py-3">
        <NavLink to="/account" onClick={onNavigate} className="block rounded-lg px-3 py-2 hover:bg-muted">
          <p className="truncate text-sm font-medium">{me?.name}</p>
          <p className="truncate text-xs text-muted-fg">{me && ROLE_LABELS[me.role]}</p>
        </NavLink>
        <button onClick={logout} className="mt-1 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-muted-fg hover:bg-muted hover:text-fg">
          <LogOut className="size-4" /> Sign out
        </button>
      </div>
    </div>
  );
}

export function Layout({ children }: { children?: ReactNode }) {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => setOpen(false), [loc.pathname]);
  return (
    <div className="flex h-full">
      <aside className="no-print hidden w-64 flex-none border-r bg-card lg:block">
        <Sidebar />
      </aside>
      {open && (
        <div className="no-print fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 bg-card shadow-xl">
            <button onClick={() => setOpen(false)} className="absolute top-4 right-3 rounded-md p-1.5 text-muted-fg hover:bg-muted" aria-label="Close menu"><X className="size-5" /></button>
            <Sidebar onNavigate={() => setOpen(false)} />
          </aside>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="no-print sticky top-0 z-30 flex items-center gap-3 border-b bg-card px-4 py-3 lg:hidden">
          <button onClick={() => setOpen(true)} className="-ml-1 rounded-md p-1.5 hover:bg-muted" aria-label="Open menu"><Menu className="size-5" /></button>
          <img src="/icon.svg" alt="" className="size-6" />
          <span className="font-semibold">BluePrint</span>
        </header>
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6 sm:py-6">{children ?? <Outlet />}</div>
        </main>
      </div>
    </div>
  );
}
